/**
 * Offline message queue with IndexedDB persistence.
 * 
 * Encrypted payloads are stored in the pending queue.
 * Plaintext is stored separately in the existing decrypted_messages store.
 * Retry uses exponential backoff (1s, 2s, 4s, 8s, max 30s), max 5 retries.
 * Background Sync: registers a sync event so the service worker can replay
 * queued messages even when the app is backgrounded.
 */

import { createLogger } from '@/lib/logger';

const log = createLogger('offline-queue');

const DB_NAME = 'elefa-msg-store';
const DB_VERSION = 2;
const PENDING_STORE = 'pending_messages';

export type PendingStatus = 'pending' | 'sending' | 'failed';

export interface PendingMessage {
  local_id: string;
  room_id: string;
  encrypted_payload: string;
  plaintext_content: string;
  message_type: string;
  metadata: Record<string, unknown>;
  created_at: string;
  retry_count: number;
  status: PendingStatus;
  encryption_version: number | null;
  /** JWT auth token captured at queue time for background sync */
  auth_token?: string;
  /** Supabase URL for background sync */
  supabase_url?: string;
  /** Supabase anon key for background sync */
  supabase_anon_key?: string;
}

interface SyncManager {
  register: (tag: string) => Promise<void>;
}
type ServiceWorkerRegistrationWithSync = ServiceWorkerRegistration & { sync?: SyncManager };

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // Existing store from v1
      if (!db.objectStoreNames.contains('decrypted_messages')) {
        const store = db.createObjectStore('decrypted_messages', { keyPath: 'id' });
        store.createIndex('room_id', 'room_id', { unique: false });
      }
      // New pending messages store in v2
      if (!db.objectStoreNames.contains(PENDING_STORE)) {
        const store = db.createObjectStore(PENDING_STORE, { keyPath: 'local_id' });
        store.createIndex('room_id', 'room_id', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Queue an encrypted message for sending and register background sync. */
export async function queueMessage(msg: PendingMessage): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(PENDING_STORE, 'readwrite');
    tx.objectStore(PENDING_STORE).put(msg);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();

    // Register background sync so SW replays when connectivity returns
    await registerBackgroundSync();
  } catch (e) {
    log.warn('Queue write failed:', e);
  }
}

/** Register a one-shot background sync event. */
async function registerBackgroundSync(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = (await navigator.serviceWorker.ready) as ServiceWorkerRegistrationWithSync;
    if (reg.sync) {
      await reg.sync.register('replay-pending-messages');
      log.debug('Background sync registered');
    }
  } catch (e) {
    // Background Sync API not supported - foreground retry still works
    log.debug('Background sync not available:', e);
  }
}

/** Get all pending messages for a specific room. */
export async function getPendingMessages(roomId: string): Promise<PendingMessage[]> {
  try {
    const db = await openDB();
    const tx = db.transaction(PENDING_STORE, 'readonly');
    const index = tx.objectStore(PENDING_STORE).index('room_id');
    const req = index.getAll(roomId);
    const result = await new Promise<PendingMessage[]>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return result;
  } catch {
    return [];
  }
}

/** Get all pending/failed messages across all rooms. */
export async function getAllPending(): Promise<PendingMessage[]> {
  try {
    const db = await openDB();
    const tx = db.transaction(PENDING_STORE, 'readonly');
    const req = tx.objectStore(PENDING_STORE).getAll();
    const result = await new Promise<PendingMessage[]>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return result.filter(m => m.status === 'pending' || m.status === 'failed');
  } catch {
    return [];
  }
}

/** Update the status of a pending message. */
export async function updatePendingStatus(localId: string, status: PendingStatus, retryCount?: number): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(PENDING_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_STORE);
    const req = store.get(localId);
    await new Promise<void>((resolve, reject) => {
      req.onsuccess = () => {
        const msg = req.result;
        if (msg) {
          msg.status = status;
          if (retryCount !== undefined) msg.retry_count = retryCount;
          store.put(msg);
        }
        resolve();
      };
      req.onerror = () => reject(req.error);
    });
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (e) {
    log.warn('Status update failed:', e);
  }
}

/** Remove a pending message after successful send. */
export async function removePending(localId: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(PENDING_STORE, 'readwrite');
    tx.objectStore(PENDING_STORE).delete(localId);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (e) {
    log.warn('Remove failed:', e);
  }
}

/** Calculate exponential backoff delay. */
export function getBackoffDelay(retryCount: number): number {
  return Math.min(1000 * Math.pow(2, retryCount), 30000);
}

/** Max retries before marking as permanently failed. */
export const MAX_RETRIES = 5;
